import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import DataTable from "../../components/DataTable";
import Swal from "sweetalert2";
import { api } from "../../axios/api";
import Modal from "react-bootstrap/Modal";
import Select from "react-select";
import { Landmark, Plus, Edit, Trash2, CreditCard } from "lucide-react";

const CoordenadasBancariasTab = () => {
  const user = useSelector((state) => state.user.user);
  const empresaId = user?.empresa_id;
  const language = useSelector((state) => state.language?.currentLanguage || "pt-AO");

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [coordData, setCoordData] = useState({});
  const [data, setData] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);

  const texts = {
    "pt-AO": {
      title: "Coordenadas Bancárias",
      btnAdd: "Adicionar",
      btnEdit: "Editar",
      btnDelete: "Eliminar",
      colBank: "BANCO",
      colIban: "IBAN",
      colAccount: "Nº CONTA",
      colStatus: "STATUS",
      modalNew: "NOVA COORDENADA BANCÁRIA",
      modalEdit: "EDITAR COORDENADA",
      labelBank: "BANCO",
      labelIban: "IBAN (PADRÃO ANGOLANO)",
      labelAccount: "NÚMERO DA CONTA",
      labelActive: "COORDENADA ATIVA",
      placeholderSelect: "Selecione...",
      btnSave: "Gravar Dados",
      btnClose: "Fechar",
      saving: "Gravando...",
      confirmDelete: "Confirmar Eliminação",
      confirmText: "Deseja realmente eliminar esta coordenada bancária?",
      successSave: "Dados bancários guardados.",
      successDelete: "Coordenada removida com sucesso.",
      error: "Falha na operação.",
      selectInfo: "Selecione uma coordenada na tabela primeiro."
    },
    "en-US": {
      title: "Bank Coordinates",
      btnAdd: "Add New",
      btnEdit: "Edit",
      btnDelete: "Delete",
      colBank: "BANK",
      colIban: "IBAN",
      colAccount: "ACCOUNT NO.",
      colStatus: "STATUS",
      modalNew: "NEW BANK COORDINATE",
      modalEdit: "EDIT COORDINATE",
      labelBank: "BANK",
      labelIban: "IBAN (ANGOLAN STANDARD)",
      labelAccount: "ACCOUNT NUMBER",
      labelActive: "ACTIVE COORDINATE",
      placeholderSelect: "Select...",
      btnSave: "Save Data",
      btnClose: "Close",
      saving: "Saving...",
      confirmDelete: "Confirm Delete",
      confirmText: "Are you sure you want to delete this bank coordinate?",
      successSave: "Bank data saved.",
      successDelete: "Coordinate successfully removed.",
      error: "Operation failed.",
      selectInfo: "Select a coordinate from the table first."
    },
    "zh-CN": {
      title: "银行账户信息",
      btnAdd: "添加",
      btnEdit: "编辑",
      btnDelete: "删除",
      colBank: "银行",
      colIban: "IBAN",
      colAccount: "账号",
      colStatus: "状态",
      modalNew: "新增银行账户",
      modalEdit: "编辑账户信息",
      labelBank: "银行",
      labelIban: "IBAN (安哥拉标准)",
      labelAccount: "银行账号",
      labelActive: "激活账户",
      placeholderSelect: "请选择...",
      btnSave: "保存数据",
      btnClose: "关闭",
      saving: "正在保存...",
      confirmDelete: "确认删除",
      confirmText: "您确定要删除此银行账户信息吗？",
      successSave: "银行数据已保存。",
      successDelete: "坐标已成功移除。",
      error: "操作失败。",
      selectInfo: "请先从表格中选择一个账户。"
    },
    "fr-FR": {
      title: "Coordonnées Bancaires",
      btnAdd: "Ajouter",
      btnEdit: "Modifier",
      btnDelete: "Supprimer",
      colBank: "BANQUE",
      colIban: "IBAN",
      colAccount: "N° COMPTE",
      colStatus: "STATUT",
      modalNew: "NOUVELLE COORDONNÉE BANCAIRE",
      modalEdit: "MODIFIER COORDONNÉE",
      labelBank: "BANQUE",
      labelIban: "IBAN (STANDARD ANGOLAIS)",
      labelAccount: "NUMÉRO DE COMPTE",
      labelActive: "COORDONNÉE ACTIVE",
      placeholderSelect: "Sélectionner...",
      btnSave: "Enregistrer",
      btnClose: "Fermer",
      saving: "Enregistrement...",
      confirmDelete: "Confirmer la Suppression",
      confirmText: "Voulez-vous vraiment supprimer cette coordonnée bancaire ?",
      successSave: "Données bancaires enregistrées.",
      successDelete: "Coordonnée supprimée avec succès.",
      error: "Échec de l'opération.",
      selectInfo: "Veuillez sélectionner une coordonnée dans le tableau."
    },

    "ar-SA": {
      title: "البيانات البنكية",
      btnAdd: "إضافة",
      btnEdit: "تعديل",
      btnDelete: "حذف",
      colBank: "البنك",
      colIban: "IBAN",
      colAccount: "رقم الحساب",
      colStatus: "الحالة",
      modalNew: "إضافة بيانات بنكية",
      modalEdit: "تعديل البيانات",
      labelBank: "البنك",
      labelIban: "IBAN (المعيار الأنغولي)",
      labelAccount: "رقم الحساب",
      labelActive: "بيانات نشطة",
      placeholderSelect: "اختر...",
      btnSave: "حفظ البيانات",
      btnClose: "إغلاق",
      saving: "جارٍ الحفظ...",
      confirmDelete: "تأكيد الحذف",
      confirmText: "هل أنت متأكد من حذف هذه البيانات البنكية؟",
      successSave: "تم حفظ البيانات البنكية.",
      successDelete: "تم حذف البيانات بنجاح.",
      error: "فشلت العملية.",
      selectInfo: "يرجى اختيار بيانات من الجدول أولاً."
    }
  };

  const t = texts[language] || texts["pt-AO"];

  const fetchCoordenadas = async () => {
    try {
      const response = await api.get(`/config/coordenadas-bancarias/empresa/${empresaId}`);
      setData(response.data);
    } catch (error) {
      console.error("Erro:", error);
    }
  };

  const fetchBancos = async () => {
    try {
      const response = await api.get("/config/bancos");
      setBancos(response.data.map((b) => ({ value: b.id, label: b.nome })));
    } catch (error) {
      console.error("Erro:", error);
    }
  };

  useEffect(() => {
    if (empresaId) {
      fetchCoordenadas();
      fetchBancos();
    }
  }, [empresaId]);

  const formatIBAN = (value) => {
    if (!value) return "";
    const cleanValue = value.replace(/[^A-Z0-9]/gi, "").toUpperCase();
    const limitedValue = cleanValue.substring(0, 21);
    return limitedValue.match(/.{1,4}/g)?.join(".") || limitedValue;
  };

  const handleSelectRow = (e) => {
    const tr = e.target.closest("tr");
    if (tr && tr.parentElement.tagName === "TBODY") {
      const firstCell = tr.cells[0];
      if (firstCell) {
        const id = parseInt(firstCell.innerText);
        if (!isNaN(id)) {
          setSelectedId(id);
          const allRows = tr.parentElement.querySelectorAll("tr");
          allRows.forEach(row => row.classList.remove("win10-row-selected"));
          tr.classList.add("win10-row-selected");
        }
      }
    }
  };

  const handleOpenAddModal = () => {
    setIsEditing(false);
    setCoordData({ empresa_id: empresaId, ativo: true, banco_id: null, iban: "", numero_conta: "" });
    setShowModal(true);
  };

  const handleOpenEditModal = () => {
    if (!selectedId) {
      return Swal.fire({ title: "Info", text: t.selectInfo, icon: "info", confirmButtonColor: "#0078d4" });
    }
    const coord = data.find((c) => c.id === selectedId);
    if (coord) {
      setIsEditing(true);
      setCoordData({ ...coord, iban: formatIBAN(coord.iban) });
      setShowModal(true);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    Swal.fire({
      title: t.confirmDelete,
      text: t.confirmText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d13438",
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/config/coordenadas-bancarias/${selectedId}`);
          setData(data.filter((c) => c.id !== selectedId));
          setSelectedId(null);
          Swal.fire("OK", t.successDelete, "success");
        } catch (error) {
          Swal.fire("Error", t.error, "error");
        }
      }
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { 
      ...coordData, 
      iban: coordData.iban.replace(/\./g, ""),
      empresa_id: empresaId 
    };

    try {
      if (isEditing) {
        await api.put(`/config/coordenadas-bancarias/${coordData.id}`, payload);
      } else {
        await api.post("/config/coordenadas-bancarias", payload);
      }
      fetchCoordenadas();
      setShowModal(false);
      setSelectedId(null);
      Swal.fire("OK", t.successSave, "success");
    } catch (e) {
      Swal.fire("Error", t.error, "error");
    } finally { setLoading(false); }
  };

  const columns = [
    { title: "ID", data: "id" },
    { title: t.colBank, data: null, render: (d, t, row) => row.banco?.nome || "-" },
    { 
      title: t.colIban, 
      data: "iban",
      render: (val) => `<span class="fw-bold font-monospace">${formatIBAN(val)}</span>` 
    },
    { title: t.colAccount, data: "numero_conta" },
    { 
      title: t.colStatus, 
      data: "ativo", 
      render: (val) => val ? `<span class="text-success fw-bold">ACTIVE</span>` : `<span class="text-muted">INACTIVE</span>` 
    },
  ];

  return (
    <>
      <style>{`
        .win10-card { background: #fff; border: 1px solid #e1e1e1; border-radius: 0px; }
        .win10-header { border-bottom: 1px solid #eee; padding: 15px 20px; background: #fff; }
        .win10-row-selected { background-color: #0078d4 !important; color: #fff !important; }
        .win10-row-selected td { color: #fff !important; }
        .win10-btn { border-radius: 0px; font-size: 13px; padding: 6px 16px; border: 1px solid transparent; }
        .win10-btn-primary { background: #0078d4; color: #fff; }
        .win10-btn-outline { background: #fff; border: 1px solid #8a8886; color: #323130; }
        .win10-label { font-size: 11px; font-weight: 700; color: #666; text-transform: uppercase; margin-bottom: 5px; }
        .win10-modal-content { border-radius: 0; border: 1px solid #0078d4; }
      `}</style>

      <div className="animate__animated animate__fadeIn">
        <div className="win10-card shadow-sm">
          <div className="win10-header d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <Landmark size={16} className="text-primary" />
              <span className="text-muted small fw-bold text-uppercase">{t.title}</span>
            </div>
            <div className="d-flex gap-1">
              <button className="win10-btn win10-btn-primary d-flex align-items-center gap-2" onClick={handleOpenAddModal}>
                <Plus size={14} /> {t.btnAdd}
              </button>
              <button className="win10-btn win10-btn-outline d-flex align-items-center gap-2" onClick={handleOpenEditModal}>
                <Edit size={14} /> {t.btnEdit}
              </button>
              <button className="win10-btn win10-btn-outline text-danger d-flex align-items-center gap-2" onClick={handleDelete}>
                <Trash2 size={14} /> {t.btnDelete}
              </button>
            </div>
          </div>
          
          <div className="card-body p-0" onClick={handleSelectRow}>
            <div className="p-3">
              <DataTable columns={columns} data={data} className="win10-table table-sm" />
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static" contentClassName="win10-modal-content">
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fs-6 fw-bold text-dark text-uppercase">
            {isEditing ? t.modalEdit : t.modalNew}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSave}>
          <Modal.Body className="px-4">
            <div className="row g-3">
              <div className="col-12">
                <label className="win10-label">{t.labelBank}</label>
                <Select
                  options={bancos}
                  value={bancos.find((b) => b.value === coordData.banco_id)}
                  onChange={(s) => setCoordData({ ...coordData, banco_id: s.value })}
                  styles={{ control: (b) => ({ ...b, borderRadius: 0, borderColor: '#8a8886', boxShadow: 'none' }) }}
                  placeholder={t.placeholderSelect}
                  required
                />
              </div>
              <div className="col-12">
                <label className="win10-label">{t.labelIban}</label>
                <div className="input-group">
                  <span className="input-group-text bg-light rounded-0 border-secondary"><CreditCard size={14}/></span>
                  <input 
                    type="text" 
                    className="form-control rounded-0 border-secondary fw-bold font-monospace" 
                    value={coordData.iban || ""} 
                    onChange={(e) => setCoordData({ ...coordData, iban: formatIBAN(e.target.value) })}
                    placeholder="AO06.0000..."
                    required 
                  />
                </div>
              </div>
              <div className="col-12">
                <label className="win10-label">{t.labelAccount}</label>
                <input 
                  type="text" 
                  className="form-control rounded-0 border-secondary" 
                  value={coordData.numero_conta || ""} 
                  onChange={(e) => setCoordData({ ...coordData, numero_conta: e.target.value })}
                  required 
                />
              </div>
              <div className="col-12">
                <div className="form-check form-switch mt-2">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    checked={coordData.ativo || false} 
                    onChange={(e) => setCoordData({ ...coordData, ativo: e.target.checked })} 
                  />
                  <label className="form-check-label small fw-bold">{t.labelActive}</label>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 bg-light">
            <button type="button" className="win10-btn win10-btn-outline" onClick={() => setShowModal(false)}>{t.btnClose}</button>
            <button type="submit" className="win10-btn win10-btn-primary px-4 fw-bold" disabled={loading}>
              {loading ? t.saving : t.btnSave}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};

export default CoordenadasBancariasTab;